import { Button, Grid, Typography, useTheme } from '@mui/material';
import { SGTooltip } from 'components/core';
import { getTradeDateDisplayText } from 'util/iVol';
import SearchIcon from '@mui/icons-material/Search';
import { Dayjs } from 'dayjs';
import { useMemo, RefObject } from 'react';
import { MatrixDataMode } from 'types';
import GradientLegend from './GradientLegend';
import { negativeTrendColorState, positiveTrendColorState } from 'states';
import { useRecoilValue } from 'recoil';

interface FixedStrikeTableLegendProps {
  selectedSym: string;
  scrollToCurrentPrice: RefObject<(() => boolean) | null>;
  currentPrice: number;
  currentDate: Dayjs;
  currentTimezone: string;
  isCompareMode: boolean;
  compareDate: Dayjs;
  matrixDataMode: MatrixDataMode;
  minVol: number;
  maxVol: number;
  volColorBound: number;
  distanceScoreBound: number;
  matrixDefaultBgColor: string;
}

const FixedStrikeTableLegend = ({
  selectedSym,
  scrollToCurrentPrice,
  currentPrice,
  currentDate,
  currentTimezone,
  isCompareMode,
  compareDate,
  matrixDataMode,
  minVol,
  maxVol,
  volColorBound,
  distanceScoreBound,
  matrixDefaultBgColor,
}: FixedStrikeTableLegendProps) => {
  const theme = useTheme();

  const serverPositiveTrendColor = useRecoilValue(positiveTrendColorState);
  const serverNegativeTrendColor = useRecoilValue(negativeTrendColorState);

  const gradientLegend = useMemo(() => {
    let min = minVol;
    let max = maxVol;
    let label = 'Implied Vol';
    let format: 'percent' | 'float' | undefined = undefined;
    let customMinLabel = undefined;
    let customMaxLabel = undefined;
    let minColor = serverNegativeTrendColor;
    let maxColor = serverPositiveTrendColor;

    switch (matrixDataMode) {
      case MatrixDataMode.COMPARE_MODE:
        min = volColorBound * -1;
        max = volColorBound;
        label = 'Implied Vol Time-Scaled Difference';
        format = 'percent';
        break;
      case MatrixDataMode.STATS_MODE:
        min = distanceScoreBound * -1;
        max = distanceScoreBound;
        label = 'Implied Vol Z-Score';
        break;
      case MatrixDataMode.SKEW_PREMIUM_MODE:
        min = 0;
        max = 1;
        minColor = matrixDefaultBgColor;
        maxColor = theme.palette.sgGreen;
        label = 'Vol Skew Premium';
        customMinLabel = 'Low';
        customMaxLabel = 'High';
        break;
      default:
        minColor = matrixDefaultBgColor;
        maxColor = theme.palette.sgGreen;
        format = 'percent';
    }

    return (
      <GradientLegend
        min={min}
        max={max}
        customMinLabel={customMinLabel}
        customMaxLabel={customMaxLabel}
        minColor={minColor}
        maxColor={maxColor}
        label={label}
        format={format}
      />
    );
  }, [
    theme.palette,
    matrixDataMode,
    minVol,
    maxVol,
    serverNegativeTrendColor,
    serverPositiveTrendColor,
    matrixDefaultBgColor,
    volColorBound,
    distanceScoreBound,
  ]);

  return (
    <Grid container sx={{ marginBottom: '10px' }} display="flex">
      <Grid
        item
        xs={12}
        gap={4}
        container
        sx={{
          width: '100%',
          justifyContent: {
            xs: 'center',
            md: 'flex-start',
          },
        }}
      >
        <Typography
          sx={{
            color: theme.palette.getContrastText(
              theme.palette.background.default,
            ),
            fontSize: {
              xs: '10px',
              md: '13px',
            },
            display: 'flex',
            alignItems: 'center',
            minWidth: 'fit-content',
            wrap: 'nowrap',
          }}
        >
          {selectedSym} Current Price:{' '}
        </Typography>
        <SGTooltip
          PopperProps={{
            sx: {
              maxWidth: 160,
              '& .MuiTooltip-tooltip': {
                fontSize: '12px',
              },
            },
          }}
          title="Click to view strikes closest to current price"
        >
          <Button
            variant="contained"
            color="primary"
            disableElevation
            sx={{
              fontSize: {
                xs: '10px',
                md: '13px',
              },
              height: '35px',
            }}
            onClick={() => {
              if (scrollToCurrentPrice.current != null) {
                scrollToCurrentPrice.current();
              }
            }}
            endIcon={<SearchIcon />}
          >
            {currentPrice && currentPrice?.toFixed(2)}
          </Button>
        </SGTooltip>
        <Typography
          sx={{
            color: theme.palette.getContrastText(
              theme.palette.background.default,
            ),
            fontSize: {
              xs: '10px',
              md: '13px',
            },
            display: 'flex',
            alignItems: 'center',
            marginRight: 5,
          }}
        >
          {getTradeDateDisplayText(currentDate, currentTimezone)}
          {isCompareMode && ` vs ${compareDate.format('YYYY-MM-DD')}`}
        </Typography>
        {gradientLegend}
      </Grid>
    </Grid>
  );
};

export default FixedStrikeTableLegend;
